// Package imports:
import React, { useMemo } from "react";
import { Form, Formik, useFormikContext } from "formik";
import * as yup from "yup";
// Component imports:
import Modal from "../../../ui-elements/Modal/Modal";
import Button from "../../../ui-elements/Button/Button";

import { GET_KELDAN_API_URL } from "../../../services/config";
import { PepWatchListData, PepWatchResponse } from "../../../types/PepTypes";
import { IKeldanApiResponse } from "../../../types/KeldanTypes";
import { AlertType } from "../../../ui-elements/Alert/Alert";
import Bugsnag from "@bugsnag/js";
import Link from "../../../ui-elements/Link/Link";
import Input from "../../../ui-elements/Input/Formik/Input";
import { CountryList } from "../../../assets/staticData/CountryList";
import { DatePickerField } from "../../../ui-elements/DatePicker/Formik/DatePicker";
import { formatDateStringToYYYYMMDD } from "../../../services/pepWatchUtils";
import Radio from "../../../ui-elements/Radio/Radio";
import DropSelectSearch from "../../../ui-elements/DropSelect/DropSelectSearch/DropSelectSearch";

interface IProps {
  personData?: PepWatchListData;
  show: boolean;
  close(): void;
  setToastMessage(message: string): void;
  setToastType(type: AlertType): void;
  setToastShow(show: boolean): void;

  //success callback - triggers refresh of the table in parent component
  onSuccess(): void;
}

const validationSchema = yup.object().shape({
  firstName: yup.string().required("Fornafn er nauðsynlegt"),
  lastName: yup.string().required("Eftirnafn er nauðsynlegt"),
  middleName: yup.string(),
  originalName: yup.string(),
  countryCode: yup
    .string()
    .required("Land er nauðsynlegt")
    .oneOf(
      CountryList.map((country) => country.value),
      "Vinsamlegast veldu land"
    ),
  dateOfBirth: yup.string().test("is-valid-date", "Snið dagsetningar skal vera YYYY-MM-DD", (value) => {
    // If no value provided, it's not optional so return false
    if (!value) return false;
    // Validate the date format and ensure it's not in the future
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return false; // Invalid date
      return date <= new Date(); // Not in the future
    } catch (e) {
      return false;
    }
  }),
  gender: yup.string().nullable().oneOf(["Male", "Female", null], "Vinsamlegast veldu kyn"),
});

const PepAddForeignWatchModal: React.FC<IProps> = ({
  show,
  close,
  setToastMessage,
  setToastType,
  setToastShow,
  personData,
  onSuccess,
}) => {
  const handleSubmit = async (values: IFormValues) => {
    try {
      //mandatory fields
      const requestQuery = new URLSearchParams({
        personId: personData?.id.personId ?? "",
        FirstName: values.firstName,
        LastName: values.lastName,
        SanctionsFrequency: "quarterly",
        // reference: modalReference,
      });
      if (values.middleName) requestQuery.append("MiddleName", values.middleName);
      if (values.originalName) requestQuery.append("OriginalName", values.originalName);
      if (values.countryCode) requestQuery.append("CountryCode", values.countryCode);
      if (values.gender) requestQuery.append("Gender", values.gender);
      if (values.dateOfBirth) requestQuery.append("DateOfBirth", formatDateStringToYYYYMMDD(values.dateOfBirth) ?? "");
      const url = `${GET_KELDAN_API_URL()}/Pep/UpdatePepWatchList`;
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: requestQuery,
      });
      const responseBody: IKeldanApiResponse<PepWatchResponse<PepWatchListData[]>> = await response.json();
      if (responseBody.success) {
        onSuccess();
        setToastMessage("Vöktun á viðskiptavini breytt");
        setToastType("success");
        setToastShow(true);
        close();
      } else {
        setToastMessage("Vöktun á viðskiptavini ekki breytt");
        setToastType("alert");
        setToastShow(true);
      }
    } catch (e) {
      if (e instanceof Error) Bugsnag.notify(e);
      setToastMessage("Vöktun á viðskiptavini ekki breytt");
      setToastType("error");
      setToastShow(true);
    }
  };
  const subFormComponent = useMemo(() => {
    return (props: { values: IFormValues; data: PepWatchListData }) => (
      <SubFormComponent values={props.values} close={close} />
    );
  }, []);

  const modalBody = () => {
    return (
      <div>
        <Formik
          initialValues={{
            firstName: personData?.personFirstName ?? "",
            middleName: personData?.personMiddleName,
            lastName: personData?.personLastName ?? "",
            originalName: personData?.personOriginalName,
            countryCode: personData?.personCountryCode,
            dateOfBirth: personData?.personDateOfBirth,
            gender: personData?.personGender,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          component={subFormComponent}
        />
      </div>
    );
  };
  return <Modal show={show} onHide={close} hasCloseButton body={modalBody()} />;
};

export default PepAddForeignWatchModal;

interface IFormValues {
  firstName: string;
  middleName?: string;
  lastName: string;
  originalName?: string;
  countryCode?: string;
  dateOfBirth?: string;
  gender?: string;
}

// Update SubFormComponent to include our FormObserver
const SubFormComponent: React.FC<{ values: IFormValues; close(): void }> = ({ values, close }) => {
  const { setFieldValue, getFieldMeta } = useFormikContext<IFormValues>();
  const countryCodeFieldMeta = getFieldMeta("countryCode");

  return (
    <>
      <Form>
        <div className="form__body">
          <h3>Alþjóðlegir þvingunarlistar, PEP og Adverse Media</h3>
          <p className="paragraph--bold">Þú ert að fara að vakta einstakling á móti alþjóðlegum válistum. Sjálfvirk uppfletting fer fram ársfjórðungslega (e. quarterly monitoring).</p>
          <p className="paragraph--bold">
            Verð eru samkvæmt{" "}
            <Link linkSize="18" url="/Verdskra#Gagnaleit">
              verðskrá Keldunnar
            </Link>{" "}
            eða samningi hverju sinni.
          </p>
          <p>Ekki er þörf að fylla inn alla reiti en ítarlegri upplýsingar auka líkur á að einstaklingur finnist.</p>
          <div className="form__row">
            <div className="form__col">
              <Input id="firstName" name="firstName" label="Fornafn" value={values.firstName} />
            </div>
            <div className="form__col">
              <Input id="middleName" name="middleName" label="Millinafn" value={values.middleName} />
            </div>
            <div className="form__col">
              <Input id="lastName" name="lastName" label="Eftirnafn" value={values.lastName} />
            </div>
          </div>
          <p>
            Upprunalegt nafn getur annað hvort verið fullt nafn einstaklings í öðru stafrófi en latnesku/rómönsku (til
            dæmis arabíska eða kínverska) eða fullt nafn í latnesku/rómönsku stafrófi ef ekki er hægt að skipta nafninu
            í fornafn, millinafn og eftirnafn.
          </p>
          <div className="form__row">
            <div className="form__col">
              <Input id="originalName" name="originalName" label="Upprunalegt nafn" value={values.originalName} />
            </div>
            <div className="form__col">
              <DropSelectSearch
                usePortal={true}
                error={
                  countryCodeFieldMeta.touched && countryCodeFieldMeta.error ? countryCodeFieldMeta.error : undefined
                }
                size="lg"
                label="Land"
                options={CountryList}
                defaultValue={values.countryCode}
                onChange={(cc: string) => {
                  setFieldValue("countryCode", cc);
                }}
              />
            </div>
          </div>
          <p>Fæðingardagur verður að vera á forminu YYYY-MM-DD. Til dæmis væri 13. júlí 1988 skrifað: 1988-07-13.</p>
          <div className="form__row">
            <div className="form__col" style={{ display: "flex" }}>
              <DatePickerField
                dateFormat="yyyy-MM-dd"
                value={values.dateOfBirth ?? ""}
                label="Fæðingardagur"
                name="dateOfBirth"
                id="dateOfBirth"
                placeholder="Fæðingardagur"
                maxDate={new Date()}
              />
              <div style={{ display: "flex", gap: "20px", alignItems: "center", marginLeft: "40px" }}>
                <span>
                  <strong>Kyn: </strong>
                </span>
                <Radio
                  id="Male"
                  name="gender"
                  value="Male"
                  defaultValue={values.gender ?? ""}
                  onChange={(e) => setFieldValue("gender", e.target.value)}
                >
                  Karl
                </Radio>
                <Radio
                  id="Femal"
                  name="gender"
                  value="Female"
                  defaultValue={values.gender ?? ""}
                  onChange={(e) => setFieldValue("gender", e.target.value)}
                >
                  Kona
                </Radio>
              </div>
            </div>
          </div>
        </div>
        <div className="form__actions">
          <Button type="button" buttonType="secondary" size="lg" onClick={() => close()}>
            Hætta við
          </Button>
          <Button type="submit" buttonType="primary" size="lg">
            Staðfesta
          </Button>
        </div>
      </Form>
    </>
  );
};
